import React from "react";
import { format } from "date-fns";

import type { Chart as ChartJS } from "chart.js";
import type { SessionMetricPoint } from "api/types/charts";
import TargetChart, {
  type ChartMarkerLine,
  type ClinicalPhaseZone,
  type TargetChartTooltipPoint,
  type XAxisMode,
} from "../TargetChart";
import {
  buildLineSeries,
  formatMetricValueLabel,
  parseSessionDate,
  type LineChartConfig,
} from "../metricChartRegistry";

export interface TargetMetricChartProps {
  /** Resolved line-chart config for the selected metric (from the registry). */
  config: LineChartConfig;
  /** The target's session metrics (oldest first). */
  metrics: SessionMetricPoint[];
  /** Mastery criterion line — only drawn for percentage metrics. */
  masteryCriterion?: number;
  /** Clinical phase background zones. */
  phaseZones?: ClinicalPhaseZone[];
  /** Clinician-placed markers drawn as vertical annotation lines. */
  markers?: ChartMarkerLine[];
  /** Whether the x-axis shows dates or session numbers. Defaults to "date". */
  xAxisMode?: XAxisMode;
  /** X-axis mode toggle callback. */
  onToggleXAxisMode?: (mode: XAxisMode) => void;
  /** Whether the trend line overlay is shown. */
  showTrendLine?: boolean;
  /** Trend line toggle callback. */
  onToggleTrendLine?: (checked: boolean) => void;
  /** Add-marker callback. */
  onAddMarker?: () => void;
  /** Forwarded to TargetChart — "Edit marker" picked on a marker line. */
  onEditMarker?: (markerId: number) => void;
  /** Forwarded to TargetChart — "Delete marker" picked on a marker line. */
  onDeleteMarker?: (markerId: number) => void;
  /** Fires with the hovered data index (parallel to `metrics`), or null on leave. */
  onActivePointChange?: (index: number | null) => void;
  /** Forwarded to TargetChart — fires with the chart.js instance on mount/unmount. */
  onChartReady?: (chart: ChartJS<"line"> | null) => void;
}

/**
 * Renders the chart for a selected metric tab by reading its registry config.
 * Every current category is a session line chart (`TargetChart`); the config's
 * `kind` drives the y-axis scaling, the optional value transform, and whether a
 * mastery line is drawn:
 *
 * - `percentage-line` → fixed 0–100 y-axis, mastery criterion line.
 * - `count-rate-line` / `duration-line` / `latency-line` / `rating-line` →
 *   auto-scaled y-axis, no mastery line; duration/latency apply a ms→s transform.
 *
 * New non-line categories (distribution, step breakdown, …) extend the switch.
 *
 * @param props
 * @param props.config - Resolved metric chart config
 * @param props.metrics - Session metrics series
 * @param [props.masteryCriterion] - Mastery threshold (percentage metrics only)
 * @param [props.phaseZones] - Clinical phase background zones
 * @param [props.xAxisMode] - X-axis display mode; defaults to "date"
 * @param [props.onToggleXAxisMode] - X-axis mode toggle callback
 * @param [props.showTrendLine] - Trend line overlay state
 * @param [props.onToggleTrendLine] - Trend line toggle callback
 * @param [props.onAddMarker] - Add-marker callback
 * @param [props.onEditMarker] - "Edit marker" callback for a clicked marker line
 * @param [props.onDeleteMarker] - "Delete marker" callback for a clicked marker line
 * @param [props.onActivePointChange] - Hovered data index callback
 */
const TargetMetricChart: React.FC<TargetMetricChartProps> = ({
  config,
  metrics,
  masteryCriterion,
  phaseZones,
  markers,
  xAxisMode = "date",
  onToggleXAxisMode,
  showTrendLine,
  onToggleTrendLine,
  onAddMarker,
  onEditMarker,
  onDeleteMarker,
  onActivePointChange,
  onChartReady,
}) => {
  const isPercentage = config.kind === "percentage-line";
  const transform = "transform" in config ? config.transform : undefined;
  const { labels, dateLabels, data } = buildLineSeries(metrics, config.field, transform);

  // Per-point copy for the designed hover tooltip. The date row is dropped when
  // a session has no recorded date. The clinician row is left unset — the API
  // doesn't expose it yet.
  const tooltipPoints: TargetChartTooltipPoint[] = metrics.map((metric, i) => {
    const value = data[i];
    const date = parseSessionDate(metric.session_date);

    return {
      sessionLabel: `Session ${metric.session_number}`,
      dateLabel: date ? format(date, "MMMM do, yyyy") : "",
      valueLabel: value == null ? "" : formatMetricValueLabel(value, config),
    };
  });

  return (
    <TargetChart
      title={config.chartTitle}
      yAxisLabel={config.yAxisLabel}
      yScale={isPercentage ? "percentage" : "auto"}
      labels={xAxisMode === "session" ? labels : dateLabels}
      data={data}
      masteryCriterion={isPercentage ? masteryCriterion : undefined}
      phaseZones={phaseZones}
      markers={markers}
      tooltipPoints={tooltipPoints}
      xAxisMode={xAxisMode}
      onToggleXAxisMode={onToggleXAxisMode}
      showTrendLine={showTrendLine}
      onToggleTrendLine={onToggleTrendLine}
      onAddMarker={onAddMarker}
      onEditMarker={onEditMarker}
      onDeleteMarker={onDeleteMarker}
      onActivePointChange={onActivePointChange}
      onChartReady={onChartReady}
    />
  );
};

export default TargetMetricChart;
